import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  useDeleteStockItem,
  useSaveStockItem,
  useScanStock,
  useStockItems,
  useStockSuggestions,
  useStockUnits
} from '../api/queries'
import type { StockItem, StockItemInput } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { CodeScanner } from '../components/CodeScanner'
import { normalizeScanned } from '../lib/scanner'
import { StockLabel, stockItemUrl } from '../components/StockLabel'
import { Card, EmptyState, ErrorBox, Loading } from '../components/ui'
import { formatDate } from '../lib/format'

/**
 * Det fælles varekartotek (docs/DECISIONS.md beslutning 069).
 *
 * <b>Scanneren står øverst, ikke søgefeltet.</b> Den, der åbner siden i en bil,
 * har som regel noget i hånden - en æske eller en rulle - og vil vide, hvad det
 * er. Den, der sidder på kontoret og leder, kan skrive i stedet.
 */
export function StockItemsPage() {
  const { isProjectManager } = useAuth()

  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')
  const [includeInactive, setIncludeInactive] = useState(false)
  const [editing, setEditing] = useState<StockItem | 'new' | null>(null)
  const [newBarcode, setNewBarcode] = useState<string | null>(null)
  const [scanMessage, setScanMessage] = useState<string | null>(null)
  const [found, setFound] = useState<StockItem | null>(null)

  const { data: suggestions } = useStockSuggestions()
  const { data, isLoading, error } = useStockItems({
    search: search.trim() || undefined,
    category: category || undefined,
    includeInactive
  })

  const scan = useScanStock()

  async function handleCode(code: string) {
    setScanMessage(null)
    setFound(null)

    try {
      // En scannet QR indeholder en hel URL; koden pilles ud, foer opslaget.
      const result = await scan.mutateAsync(normalizeScanned(code))

      if (result.kind === 'item' && result.item) {
        setFound(result.item)
        return
      }

      // En ukendt stregkode er ikke en fejl - manden staar med en aeske, vi ikke
      // kender endnu. Tilbyd at oprette varen med stregkoden udfyldt.
      if (result.kind === 'barcode_unknown' && isProjectManager) {
        setNewBarcode(result.scannedCode)
        setEditing('new')
        return
      }

      setScanMessage(result.message ?? `"${result.scannedCode}" blev ikke fundet.`)
    } catch (err) {
      setScanMessage(err instanceof Error ? err.message : 'Opslaget kunne ikke laves.')
    }
  }

  if (editing) {
    return (
      <StockItemForm
        item={editing === 'new' ? null : editing}
        prefilledBarcode={editing === 'new' ? newBarcode : null}
        onClose={() => {
          setEditing(null)
          setNewBarcode(null)
        }}
      />
    )
  }

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h1 className="page-title" style={{ margin: 0 }}>
          Varer
        </h1>
        {isProjectManager && (
          <button className="btn btn--primary" onClick={() => setEditing('new')}>
            Ny vare
          </button>
        )}
      </div>

      <Card title="Hvad står du med?">
        <CodeScanner
          onCode={(code) => void handleCode(code)}
          busy={scan.isPending}
          hint="QR-mærkat fra os, eller stregkoden på emballagen"
        />

        {scan.isPending && <div className="small muted" style={{ marginTop: 10 }}>Slår op ...</div>}

        {scanMessage && (
          <div className="map-notice" style={{ marginTop: 12 }}>
            {scanMessage}
          </div>
        )}

        {found && (
          <div style={{ marginTop: 12 }}>
            <div className="tiny muted">Fundet</div>
            <ItemRow item={found} onOpen={isProjectManager ? () => setEditing(found) : undefined} />
          </div>
        )}
      </Card>

      <Card>
        <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            style={{ minWidth: 220, flex: 1 }}
            placeholder="Søg i navn, varenummer, stregkode"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />

          <select
            style={{ maxWidth: 200 }}
            value={category}
            onChange={(event) => setCategory(event.target.value)}
          >
            <option value="">Alle grupper</option>
            {(suggestions?.categories ?? []).map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>

          <label className="small row" style={{ gap: 6, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(event) => setIncludeInactive(event.target.checked)}
            />
            Vis udgåede
          </label>
        </div>
      </Card>

      {isLoading && <Loading />}
      {error && <ErrorBox error={error} />}

      {data && data.length === 0 && (
        <EmptyState>
          {search || category
            ? 'Ingen varer passer på det, du søgte efter.'
            : 'Der er ingen varer endnu. Opret den første, eller scan en stregkode.'}
        </EmptyState>
      )}

      {data && data.length > 0 && (
        <Card>
          <div className="stack" style={{ gap: 2 }}>
            {data.map((item) => (
              <ItemRow
                key={item.id}
                item={item}
                onOpen={isProjectManager ? () => setEditing(item) : undefined}
              />
            ))}
          </div>

          {data.length >= 100 && (
            <div className="tiny muted" style={{ marginTop: 10 }}>
              Der vises højst 100 ad gangen. Søg for at finde den rigtige.
            </div>
          )}
        </Card>
      )}
    </div>
  )
}

function ItemRow({ item, onOpen }: { item: StockItem; onOpen?: () => void }) {
  const content = (
    <>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontWeight: 600 }}>
          {item.name}
          {!item.isActive && <span className="tiny muted"> · udgået</span>}
        </div>
        <div className="tiny muted">
          {[item.supplierNumber, item.supplier, item.category].filter(Boolean).join(' · ') ||
            'Ingen varenummer'}
        </div>
        <div className="tiny muted">
          {/* Begge koder staar der: den ene bruges paa emballagen, den anden paa
              vores eget maerkat, og man ved ikke paa forhaand hvilken man har. */}
          {item.barcode ? `Stregkode ${item.barcode}` : 'Ingen stregkode'} · Kode {item.code}
        </div>
      </div>

      <div className="tiny muted" style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
        {item.costPrice === null || item.costPrice === undefined ? (
          'Pris ukendt'
        ) : (
          <>
            {item.costPrice.toLocaleString('da-DK', { minimumFractionDigits: 2 })} kr./{item.unit}
            <div>{item.priceUpdatedAt ? formatDate(item.priceUpdatedAt) : ''}</div>
          </>
        )}
      </div>
    </>
  )

  if (!onOpen) {
    return (
      <div className="row row--between device-row" style={{ gap: 12, opacity: item.isActive ? 1 : 0.55 }}>
        {content}
      </div>
    )
  }

  return (
    <button
      className="row row--between device-row"
      style={{
        gap: 12,
        opacity: item.isActive ? 1 : 0.55,
        width: '100%',
        border: 0,
        background: 'transparent',
        textAlign: 'left',
        cursor: 'pointer'
      }}
      onClick={onOpen}
    >
      {content}
    </button>
  )
}

/**
 * Opret eller ret en vare.
 *
 * <b>Stregkoden kan udfyldes på forhånd</b>, når man er kommet hertil ved at
 * scanne noget ukendt: så står manden med æsken, og systemet har allerede læst
 * koden - han skal kun skrive, hvad det er.
 */
function StockItemForm({
  item,
  prefilledBarcode,
  onClose
}: {
  item: StockItem | null
  prefilledBarcode: string | null
  onClose: () => void
}) {
  const { isAdmin } = useAuth()
  const { data: units } = useStockUnits()
  const { data: suggestions } = useStockSuggestions()

  const save = useSaveStockItem()
  const remove = useDeleteStockItem()

  const [form, setForm] = useState<StockItemInput>({
    name: item?.name ?? '',
    description: item?.description ?? '',
    barcode: item?.barcode ?? prefilledBarcode ?? '',
    supplierNumber: item?.supplierNumber ?? '',
    unit: item?.unit ?? 'stk',
    supplier: item?.supplier ?? '',
    category: item?.category ?? '',
    costPrice: item?.costPrice ?? null,
    isActive: item?.isActive ?? true
  })

  const [problem, setProblem] = useState<string | null>(null)

  function update<K extends keyof StockItemInput>(key: K, value: StockItemInput[K]) {
    setForm((current) => ({ ...current, [key]: value }))
  }

  async function submit() {
    if (!form.name.trim()) {
      setProblem('Varen skal have et navn.')
      return
    }

    setProblem(null)
    try {
      await save.mutateAsync({ id: item?.id, input: form })
      onClose()
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Varen kunne ikke gemmes.')
    }
  }

  async function drop() {
    if (!item) return
    if (!window.confirm(`Slet varen "${item.name}"? Overvej at sætte den udgået i stedet.`)) return

    try {
      await remove.mutateAsync(item.id)
      onClose()
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Varen kunne ikke slettes.')
    }
  }

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h1 className="page-title" style={{ margin: 0 }}>
          {item ? 'Rediger vare' : 'Ny vare'}
        </h1>
        <div className="btn-group">
          <button className="btn" onClick={onClose}>
            Fortryd
          </button>
          {item && isAdmin && (
            <button className="btn" onClick={() => void drop()} disabled={remove.isPending}>
              Slet
            </button>
          )}
          <button className="btn btn--primary" onClick={() => void submit()} disabled={save.isPending}>
            {save.isPending ? 'Gemmer ...' : 'Gem'}
          </button>
        </div>
      </div>

      {problem && <div className="error-box">{problem}</div>}

      {prefilledBarcode && !item && (
        <div className="map-notice">
          Stregkoden <strong>{prefilledBarcode}</strong> blev scannet og er sat ind. Skriv hvad varen
          er, så kender systemet den næste gang.
        </div>
      )}

      <Card title="Om varen">
        <div className="stack" style={{ gap: 12 }}>
          <label className="stack" style={{ gap: 4 }}>
            <span className="tiny muted">Navn</span>
            <input
              autoFocus
              value={form.name}
              onChange={(event) => update('name', event.target.value)}
              placeholder="Fx Kabelbinder 200 mm sort"
            />
          </label>

          <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
            <label className="stack" style={{ gap: 4, minWidth: 190 }}>
              <span className="tiny muted">Stregkode (EAN)</span>
              <input
                inputMode="numeric"
                value={form.barcode ?? ''}
                onChange={(event) => update('barcode', event.target.value)}
                placeholder="Fra emballagen"
              />
            </label>

            <label className="stack" style={{ gap: 4, minWidth: 160 }}>
              <span className="tiny muted">Varenummer</span>
              <input
                value={form.supplierNumber ?? ''}
                onChange={(event) => update('supplierNumber', event.target.value)}
                placeholder="Det du bestiller efter"
              />
            </label>

            <label className="stack" style={{ gap: 4, minWidth: 130 }}>
              <span className="tiny muted">Tælles i</span>
              <select value={form.unit} onChange={(event) => update('unit', event.target.value)}>
                {(units ?? []).map((unit) => (
                  <option key={unit.code} value={unit.code}>
                    {unit.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
            <label className="stack" style={{ gap: 4, minWidth: 180 }}>
              <span className="tiny muted">Leverandør</span>
              <input
                list="stock-suppliers"
                value={form.supplier ?? ''}
                onChange={(event) => update('supplier', event.target.value)}
                placeholder="Fx Solar"
              />
              <datalist id="stock-suppliers">
                {(suggestions?.suppliers ?? []).map((name) => (
                  <option key={name} value={name} />
                ))}
              </datalist>
            </label>

            <label className="stack" style={{ gap: 4, minWidth: 180 }}>
              <span className="tiny muted">Gruppe</span>
              <input
                list="stock-categories"
                value={form.category ?? ''}
                onChange={(event) => update('category', event.target.value)}
                placeholder="Fx Fittings"
              />
              <datalist id="stock-categories">
                {(suggestions?.categories ?? []).map((name) => (
                  <option key={name} value={name} />
                ))}
              </datalist>
            </label>

            <label className="stack" style={{ gap: 4, minWidth: 150 }}>
              <span className="tiny muted">Kostpris pr. enhed</span>
              <input
                inputMode="decimal"
                value={form.costPrice ?? ''}
                onChange={(event) => {
                  const raw = event.target.value.replace(',', '.').trim()
                  update('costPrice', raw === '' ? null : Number(raw))
                }}
                placeholder="Uden moms"
              />
            </label>
          </div>

          <label className="stack" style={{ gap: 4 }}>
            <span className="tiny muted">Beskrivelse</span>
            <textarea
              rows={3}
              value={form.description ?? ''}
              onChange={(event) => update('description', event.target.value)}
            />
          </label>

          <label className="row" style={{ gap: 6, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(event) => update('isActive', event.target.checked)}
            />
            <span className="small">
              I brug
              <span className="tiny muted"> - udgåede varer bliver stående, men vises ikke som standard</span>
            </span>
          </label>
        </div>
      </Card>

      {item && (
        <Card title="Vores eget mærkat">
          <p className="small muted" style={{ marginTop: 0 }}>
            Til det, der ikke har en stregkode - en pose fittings eller en afklippet længde kabel.
            Koden er <code>{item.code}</code> og kan ikke ændres; den står på mærkatet.
          </p>
          <StockLabel item={item} />

          <p className="tiny muted" style={{ marginTop: 10 }}>
            QR-koden peger på <code>{stockItemUrl(item.code)}</code>. Den kan scannes med mobilens
            eget kamera eller inde i appen. Siden kræver login.
          </p>
        </Card>
      )}
    </div>
  )
}

/**
 * Landingssiden for QR-koden paa en vare (`/v/:code`).
 *
 * Maerkatet indeholder en URL, saa mobilens eget kamera ogsaa virker - ikke kun
 * scanneren inde i appen. Har man ikke adgang, soerger den almindelige
 * login-omdirigering for resten.
 */
export function StockItemLandingPage() {
  const { code } = useParams()
  const scan = useScanStock()

  const [item, setItem] = useState<StockItem | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    void scan
      .mutateAsync(code ?? '')
      .then((result) => {
        if (cancelled) return

        if (result.kind === 'item' && result.item) {
          setItem(result.item)
        } else {
          setMessage(result.message ?? 'Varen blev ikke fundet.')
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setMessage(err instanceof Error ? err.message : 'Varen kunne ikke slaas op.')
        }
      })

    return () => {
      cancelled = true
    }
    // Koden er det eneste, opslaget afhaenger af.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code])

  if (message) {
    return (
      <Card title="Varen blev ikke fundet">
        <div className="small muted">{message}</div>
        <p className="small muted">
          Koden paa maerkatet var <code>{code}</code>.
        </p>
        <Link className="btn btn--sm" to="/stock/items">
          Til varerne
        </Link>
      </Card>
    )
  }

  if (!item) return <Loading />

  return (
    <div className="stack" style={{ gap: 16 }}>
      <Card>
        <h1 className="page-title" style={{ marginBottom: 4 }}>
          {item.name}
        </h1>
        <div className="muted small">
          {[item.supplierNumber, item.supplier, item.category].filter(Boolean).join(' · ') || 'Vare'}
        </div>

        <div className="form-grid" style={{ marginTop: 16 }}>
          <Field label="Taelles i" value={item.unitName} />
          <Field label="Stregkode" value={item.barcode ?? 'Ingen'} />
          <Field label="Vores kode" value={item.code} />
          <Field
            label="Kostpris"
            value={
              item.costPrice === null || item.costPrice === undefined
                ? 'Ukendt'
                : `${item.costPrice.toLocaleString('da-DK', { minimumFractionDigits: 2 })} kr. pr. ${item.unit}`
            }
          />
        </div>

        {item.description && (
          <div style={{ marginTop: 12 }}>
            <div className="tiny muted">Beskrivelse</div>
            <div className="small" style={{ whiteSpace: 'pre-wrap' }}>
              {item.description}
            </div>
          </div>
        )}

        <div style={{ marginTop: 14 }}>
          <Link className="btn btn--sm" to="/stock/items">
            Til varerne
          </Link>
        </div>
      </Card>
    </div>
  )
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <div className="tiny muted">{label}</div>
      <div className="small">{value || '-'}</div>
    </div>
  )
}
